import {
    type FC,
    type ReactNode,
    type MouseEventHandler,
    type ComponentProps,
    useMemo,
    useState,
    useCallback,
    useEffect,
    useRef,
    useContext,
} from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';

import {
    ScrollCarouselProvider,
    useScrollCarousel,
} from 'dibs-scroll-carousel/exports/ScrollCarouselContext.ts';
import { InnerScrollCarousel } from 'dibs-scroll-carousel/exports/InnerScrollCarousel.ts';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls.ts';
import { type ArrayElement } from 'dibs-ts-utils/exports/ArrayElement.ts';

import {
    type MediaItemPhoto,
    type MediaItemVideo,
    type MediaItemViewInRoom,
    useItemMediaItems,
} from 'dibs-image-utils/exports/useItemMediaItems.ts';
import { CarouselThumb } from './CarouselThumb.tsx';
import { Image } from './CarouselItems/Image.tsx';
import { VimeoVideo } from './CarouselItems/VimeoVideo.tsx';
import { ViewInRoom } from './CarouselItems/ViewInRoom.tsx';
import { RequestMoreImages } from './CarouselItems/RequestMoreImages.tsx';
import { Counter } from './Counter.tsx';
import { PdpRespZoomModalLoader } from '../../PdpRespZoomModal/PdpRespZoomModalLoader.tsx';
import videoTracking from '../../shared/PdpSharedVimeoVideo/tracking.ts';
import { trackMainCarouselClick, trackViewRoomDisplayed, trackArrowClick } from './tracking.ts';
import { BrowserHistoryContext } from '../../../global/BrowserHistoryContext/BrowserHistoryContext.tsx';

import {
    type PdpRespSharedHeroCarousel_item$key,
    type PdpRespSharedHeroCarousel_item$data,
} from './__generated__/PdpRespSharedHeroCarousel_item.graphql.ts';
import { type PdpRespSharedHeroCarousel_viewer$key } from './__generated__/PdpRespSharedHeroCarousel_viewer.graphql.ts';
import { type PdpRespSharedHeroCarousel_user$key } from './__generated__/PdpRespSharedHeroCarousel_user.graphql.ts';

import styles from './PdpRespSharedHeroCarousel.scss';
import dibsCss from 'dibs-css';

const THUMB_ITEMS_TO_SHOW = 6;
const THUMB_ITEMS_TO_SHOW_X_SMALL = 4;
const THUMB_ITEMS_TO_SHOW_XX_SMALL = 3;
const sizeToThumbsCount = (size: 'xxSmall' | 'xSmall' | 'small' | 'medium' | 'large'): number => {
    if (size === 'xxSmall') {
        return THUMB_ITEMS_TO_SHOW_XX_SMALL;
    } else if (size === 'xSmall') {
        return THUMB_ITEMS_TO_SHOW_X_SMALL;
    } else {
        return THUMB_ITEMS_TO_SHOW;
    }
};

type CarouselItemPhoto = MediaItemPhoto<
    NonNullable<ArrayElement<PdpRespSharedHeroCarousel_item$data['sortedPhotos']>>[]
>;
type CarouselItemVideo = MediaItemVideo<PdpRespSharedHeroCarousel_item$data['videoVimeo']>;
type CarouselItemViewInRoom = MediaItemViewInRoom<
    NonNullable<ArrayElement<PdpRespSharedHeroCarousel_item$data['viewInRoomPhotos']>>[]
>;
type CarouselItemRequestMore = { __type: 'requestMore' };

type CarouselItem =
    | CarouselItemPhoto
    | CarouselItemVideo
    | CarouselItemViewInRoom
    | CarouselItemRequestMore;

type CarouselItems = CarouselItem[];

type SlideChangeType = 'next' | 'previous' | 'other';

const isPhoto = (item: CarouselItem): item is CarouselItemPhoto =>
    '__typename' in item && item.__typename === 'ItemPhoto';

const ZoomModal: FC<{
    isOpen: boolean;
    onCloseClick: () => void;
    carouselItems: CarouselItems;
    title: string | null;
    image: string | null;
    itemId: string | null;
    userId: string | null;
    currency: string;
    size: 'xxSmall' | 'xSmall' | 'small' | 'medium' | 'large';
}> = ({ isOpen, onCloseClick, carouselItems, title, image, itemId, userId, currency, size }) => {
    const { activeSlide, setCurrentIndex } = useScrollCarousel();

    const onModalCloseClick = useCallback(
        ({ index }: $TSFixMe) => {
            onCloseClick();
            /**
             * Hack to make old carousels to be compatible with scroll carousel. Additionally
             * need to use setTimeout to make sure that all scroll preventions that are added
             * by the modal are removed before setting the index.
             */
            setTimeout(() => {
                const itemsToShow = sizeToThumbsCount(size);

                setCurrentIndex(
                    carouselItems.length > itemsToShow
                        ? {
                              index: index + itemsToShow,
                              backfillStartCount: itemsToShow,
                          }
                        : {
                              index,
                              backfillStartCount: 0,
                          }
                );
            }, 50);
        },
        [setCurrentIndex, onCloseClick, carouselItems, size]
    );

    const zoomMedia = useMemo(() => {
        return carouselItems
            .map(carouselItem => {
                if (carouselItem.__type === 'photo') {
                    return {
                        src: carouselItem.masterOrZoomPath,
                        alt: carouselItem.alt,
                    };
                } else if (carouselItem.__type === 'video') {
                    return {
                        type: 'VIMEO',
                        src: carouselItem.thumbnail,
                        thumbnail: carouselItem.thumbnail,
                        vimeoPlayerOptions: {
                            quality: '1080p',
                            id: carouselItem.vimeoId,
                            tracking: videoTracking,
                        },
                    };
                }

                return null;
            })
            .filter(filterNulls);
    }, [carouselItems]);

    return title && image && itemId ? (
        <PdpRespZoomModalLoader
            //@ts-ignore
            relayVariables={{ itemId, fetchUser: !!userId, userId }}
            isOpen={isOpen}
            totalItems={carouselItems.length}
            onCloseClick={onModalCloseClick}
            startingIndex={activeSlide - 1}
            zoomMedia={zoomMedia}
            display
            itemTitle={title}
            itemImage={image}
            itemId={itemId}
            currency={currency}
        />
    ) : null;
};

const CarouselItemWrapper: FC<{
    type: CarouselItem['__type'];
    size: 'xxSmall' | 'xSmall' | 'small' | 'medium' | 'large';
    openZoomModal: () => void;
    dataTn: string;
    children: ReactNode;
}> = ({ type, size, openZoomModal, dataTn, children }) => {
    const { activeSlide, totalSlides } = useScrollCarousel();
    const isZoomable = type === 'photo' || type === 'viewInRoom';

    const onClick: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement> = useCallback(
        event => {
            if (isZoomable) {
                trackMainCarouselClick(
                    {
                        activeSlide,
                        totalSlides,
                    },
                    event
                );
            }
            openZoomModal();
        },
        [openZoomModal, activeSlide, totalSlides, isZoomable]
    );

    return isZoomable ? (
        <Link
            className={classnames(styles.item, styles[size], 'is-zoom')}
            onClick={onClick}
            dataTn={dataTn}
        >
            {children}
        </Link>
    ) : (
        <div className={classnames(styles.item, styles[size])} data-tn={dataTn}>
            {children}
        </div>
    );
};

const viewerFragment = graphql`
    fragment PdpRespSharedHeroCarousel_viewer on Viewer {
        ...useItemMediaItems_viewer
    }
`;

const itemFragment = graphql`
    fragment PdpRespSharedHeroCarousel_item on Item {
        ...useItemMediaItems_item
        ...Image_item
        ...ViewInRoom_item
        ...RequestMoreImages_item
        sortedPhotos {
            ...useItemMediaItems_sortedPhotos
            ...CarouselThumb_sortedPhoto
            ...Image_sortedPhoto
            __typename
            masterOrZoomPath
            largePath
            alt
        }
        videoVimeo {
            ...useItemMediaItems_videoVimeo
            ...CarouselThumb_videoVimeo
            ...VimeoVideo_videoVimeo
            thumbnail(size: w100h75, includePlayButton: false, pad: false)
            vimeoId
            fileByVideoSize(size: small) {
                link
            }
        }
        viewInRoomPhotos {
            ...useItemMediaItems_viewInRoomPhotos
            ...CarouselThumb_viewInRoomPhoto
            ...ViewInRoom_viewInRoomPhoto
        }
        hasPrivateListing
        seller {
            isAccessible
        }
        serviceId
        title
        firstPhotoWebPath(size: small)
    }
`;

const userFragment = graphql`
    fragment PdpRespSharedHeroCarousel_user on User {
        ...ViewInRoom_user
        ...RequestMoreImages_user
        serviceId
    }
`;

const PdpRespSharedHeroCarouselComponent: FC<{
    item: PdpRespSharedHeroCarousel_item$key;
    user: PdpRespSharedHeroCarousel_user$key | null | undefined;
    carouselItems: CarouselItems;
    currency: string;
    disableModals?: boolean;
    size: 'xxSmall' | 'xSmall' | 'small' | 'medium' | 'large';
    afterDisplay: boolean;
    selectedSkuId?: string | null;
    children?: ReactNode;
    isPdpSiderailSellerReviewCombinedVariant?: boolean | null;
}> = ({
    item: itemRef,
    user: userRef,
    carouselItems,
    currency,
    disableModals,
    size,
    afterDisplay,
    selectedSkuId,
    children,
    isPdpSiderailSellerReviewCombinedVariant,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);

    const userId = user?.serviceId || null;

    const [isModalOpen, setIsModalOpen] = useState(false);
    const { currentState, pushState, back }: $TSFixMe = useContext(BrowserHistoryContext);

    useEffect(() => {
        const currentZoomModalOpen = !!(currentState && currentState.zoomModalOpen);
        if (currentZoomModalOpen !== isModalOpen) {
            setIsModalOpen(currentZoomModalOpen);
        }
    }, [currentState, isModalOpen]);

    const openZoomModal = useCallback(() => {
        if (disableModals) {
            return;
        }
        pushState({ zoomModalOpen: true }, '#zoomModalOpen');
        setIsModalOpen(true);
    }, [disableModals, pushState]);
    const closeZoomModal = useCallback(() => {
        if (isModalOpen) {
            back();
        }
        setIsModalOpen(false);
    }, [isModalOpen, back]);

    const { activeSlide, totalSlides, resetCurrentIndex } = useScrollCarousel();
    const slideChangeTypeRef = useRef<SlideChangeType>('other');

    const onArrowClick: NonNullable<ComponentProps<typeof InnerScrollCarousel>['onArrowClick']> =
        useCallback(({ direction }) => {
            slideChangeTypeRef.current = direction;
        }, []);

    useEffect(() => {
        const currentItem = carouselItems[activeSlide - 1];
        if (slideChangeTypeRef.current === 'next' || slideChangeTypeRef.current === 'previous') {
            trackArrowClick({
                slideType: currentItem.__type,
                activeSlide,
                totalSlides,
                arrowDirection: slideChangeTypeRef.current,
            });
        }

        if (currentItem.__type === 'viewInRoom') {
            trackViewRoomDisplayed({ activeSlide, totalSlides });
        }

        slideChangeTypeRef.current = 'other';
    }, [activeSlide, totalSlides, carouselItems]);

    const selectedSkuIdRef = useRef<string | undefined | null>(selectedSkuId);
    useEffect(() => {
        if (selectedSkuIdRef.current !== selectedSkuId) {
            selectedSkuIdRef.current = selectedSkuId;
            resetCurrentIndex();
        }
    }, [resetCurrentIndex, selectedSkuId]);

    return (
        <>
            <ZoomModal
                isOpen={isModalOpen}
                onCloseClick={closeZoomModal}
                carouselItems={carouselItems}
                title={item.title}
                image={item.firstPhotoWebPath}
                itemId={item.serviceId}
                userId={userId}
                currency={currency}
                size={size}
            />

            <div className={`${styles.wrapper} enable-click-on-preview`}>
                <div className={`${styles.thumbsWrapper} ${styles[size]}`}>
                    {afterDisplay && (
                        <>
                            <InnerScrollCarousel
                                animation="slide"
                                enableScroll
                                orientation="vertical"
                                itemsToShow={sizeToThumbsCount(size)}
                                gapSize="xSmall"
                            >
                                {carouselItems.map((carouselItem, index) => {
                                    if (carouselItem.__type === 'photo') {
                                        return (
                                            <CarouselThumb
                                                key={index}
                                                index={index}
                                                type={carouselItem.__type}
                                                size={size}
                                                sortedPhoto={carouselItem}
                                                videoVimeo={null}
                                                viewInRoomPhoto={null}
                                            />
                                        );
                                    } else if (carouselItem.__type === 'video') {
                                        return (
                                            <CarouselThumb
                                                key={index}
                                                index={index}
                                                type={carouselItem.__type}
                                                size={size}
                                                sortedPhoto={null}
                                                videoVimeo={carouselItem}
                                                viewInRoomPhoto={null}
                                            />
                                        );
                                    } else if (carouselItem.__type === 'viewInRoom') {
                                        return (
                                            <CarouselThumb
                                                key={index}
                                                index={index}
                                                type={carouselItem.__type}
                                                size={size}
                                                sortedPhoto={null}
                                                videoVimeo={null}
                                                viewInRoomPhoto={carouselItem}
                                            />
                                        );
                                    } else if (carouselItem.__type === 'requestMore') {
                                        return (
                                            <CarouselThumb
                                                key={index}
                                                index={index}
                                                type={carouselItem.__type}
                                                size={size}
                                                sortedPhoto={null}
                                                videoVimeo={null}
                                                viewInRoomPhoto={null}
                                            />
                                        );
                                    }

                                    //render empty div for unknown types to avoid breaking the carousel
                                    return <div />;
                                })}
                            </InnerScrollCarousel>
                            {isPdpSiderailSellerReviewCombinedVariant && (
                                <div className={dibsCss.mtMedium}>{children}</div>
                            )}
                        </>
                    )}
                </div>

                <div className={`${styles.displayWrapper} ${styles[size]}`}>
                    <InnerScrollCarousel
                        showArrows
                        orientation="horizontal"
                        itemsToShow={1}
                        animation="none"
                        onArrowClick={onArrowClick}
                        dataTn="pdp-image-carousel"
                    >
                        {carouselItems.map((carouselItem, index) => {
                            let child = null;
                            if (carouselItem.__type === 'photo') {
                                child = (
                                    <Image
                                        eagerLoad={index === 0}
                                        item={item}
                                        sortedPhoto={carouselItem}
                                        size={size}
                                    />
                                );
                            } else if (carouselItem.__type === 'video') {
                                child = <VimeoVideo videoVimeo={carouselItem} />;
                            } else if (
                                carouselItem.__type === 'viewInRoom' &&
                                //type guard to ensure that carouselItems[0] is a CarouselItemPhoto type
                                isPhoto(carouselItems[0])
                            ) {
                                child = (
                                    <ViewInRoom
                                        user={user}
                                        item={item}
                                        viewInRoomPhoto={carouselItem}
                                        mainImagePath={{
                                            imagePath: carouselItems[0].largePath,
                                            masterOrZoomPath: carouselItems[0].masterOrZoomPath,
                                        }}
                                    />
                                );
                            } else if (carouselItem.__type === 'requestMore') {
                                child = (
                                    <RequestMoreImages
                                        user={user}
                                        item={item}
                                        currency={currency}
                                        disableRequestMoreImagesModal={disableModals}
                                    />
                                );
                            }

                            return (
                                <CarouselItemWrapper
                                    key={index}
                                    type={carouselItem.__type}
                                    size={size}
                                    openZoomModal={openZoomModal}
                                    dataTn={`pdp-image-carousel-image-${index + 1}`}
                                >
                                    {child}
                                </CarouselItemWrapper>
                            );
                        })}
                    </InnerScrollCarousel>
                    <Counter />

                    {!isPdpSiderailSellerReviewCombinedVariant && children}
                </div>
            </div>
        </>
    );
};

export const PdpRespSharedHeroCarousel: FC<
    Omit<
        ComponentProps<typeof PdpRespSharedHeroCarouselComponent> & {
            viewer: PdpRespSharedHeroCarousel_viewer$key;
        },
        'carouselItems' | 'size'
    > & {
        size?: 'xxSmall' | 'xSmall' | 'small' | 'medium' | 'large';
    }
> = props => {
    const { viewer: viewerRef, item: itemRef, selectedSkuId, afterDisplay, size = 'large' } = props;
    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);

    const { videoVimeo, hasPrivateListing, seller } = item;

    const sortedPhotos = (item.sortedPhotos || []).filter(filterNulls);
    const viewInRoomPhotos = (item.viewInRoomPhotos || []).filter(filterNulls);
    const mediaItems = useItemMediaItems({
        viewer,
        item,
        sortedPhotos,
        videoVimeo,
        viewInRoomPhotos,
        selectedSkuId,
    });

    const showRequestMore = !hasPrivateListing && seller?.isAccessible;
    const carouselItems: CarouselItems = useMemo(() => {
        return showRequestMore ? [...mediaItems, { __type: 'requestMore' }] : mediaItems;
    }, [mediaItems, showRequestMore]);

    const mainPhoto = sortedPhotos?.[0];

    return (
        <>
            <div
                className={classnames(styles.printHide, {
                    [styles.isPdpSiderailSellerReviewCombinedVariant]:
                        props.isPdpSiderailSellerReviewCombinedVariant,
                })}
            >
                <ScrollCarouselProvider
                    isInfinite
                    totalItems={carouselItems.length}
                    largestItemsToShow={sizeToThumbsCount(size)}
                    stepSize={1}
                >
                    <PdpRespSharedHeroCarouselComponent
                        {...props}
                        carouselItems={carouselItems}
                        size={size}
                    />
                </ScrollCarouselProvider>
            </div>
            {afterDisplay && mainPhoto?.alt && mainPhoto?.masterOrZoomPath && (
                <div className={classnames(styles.printShow, styles.printWrapper, styles[size])}>
                    <img
                        src={mainPhoto.masterOrZoomPath}
                        alt={mainPhoto.alt}
                        className={classnames(styles.printImage, styles[size])}
                    />
                </div>
            )}
        </>
    );
};
